"use client";

import { useEffect, useState, useEffectEvent } from "react";
import { getGameEvents, type GameEventPoint } from "../api/getGameEvents";
import { isAbortError } from "@/lib/utils/error";
import type { ChartFestivalMarker } from "@/components/shared/rank-history";

type UseGameEventsOptions = {
  appId: number;
  enabled?: boolean;
};

export function useGameEvents({ appId, enabled = true }: UseGameEventsOptions) {
  const [events, setEvents] = useState<GameEventPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSuccess = useEffectEvent((result: GameEventPoint[]) => {
    setEvents(result);
    setIsLoading(false);
  });

  const handleError = useEffectEvent(() => {
    setIsLoading(false);
  });

  useEffect(() => {
    if (!enabled || !appId) return;

    let cancelled = false;
    setIsLoading(true);

    // No AbortController here on purpose: `getGameEvents` shares one request
    // across every chart block on the page (they all draw the same festival
    // markers), so aborting on unmount would cancel it for the blocks still
    // waiting. The `cancelled` flag already prevents a late setState.
    getGameEvents(appId)
      .then((result) => { if (!cancelled) handleSuccess(result); })
      .catch((err: Error) => {
        if (isAbortError(err)) return;
        if (!cancelled) handleError();
      });

    return () => {
      cancelled = true;
    };
  }, [appId, enabled]);

  const festivalMarkers: ChartFestivalMarker[] = events.map((e) => ({
    startMs: e.start_ts * 1000,
    endMs: e.end_ts * 1000,
    name: e.name,
    url: e.sale_page_url,
  }));

  return { festivalMarkers, isLoading };
}
